import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

export function PushNotificationsCard() {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    testNotification
  } = usePushNotifications();

  const handleToggle = async (enabled) => {
    if (enabled) {
      const success = await subscribe();
      if (success) {
        toast.success('Notifications push activées!');
      } else if (permission === 'denied') {
        toast.error('Notifications bloquées. Modifiez les paramètres de votre navigateur.');
      }
    } else {
      const success = await unsubscribe();
      if (success) {
        toast.success('Notifications push désactivées');
      }
    }
  };

  const handleTest = async () => {
    const success = await testNotification();
    if (success) {
      toast.success('Notification de test envoyée!');
    } else {
      toast.error('Échec du test. Vérifiez que les notifications sont activées.');
    }
  };

  // Not supported on this browser/device
  if (!isSupported) {
    return (
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BellOff className="w-5 h-5 text-slate-400" />
            Notifications Push
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-100 text-slate-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Non disponible sur ce navigateur</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-5 h-5 text-violet-500" />
            Notifications Push
          </CardTitle>
          {isSubscribed && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Actif
            </Badge>
          )}
        </div>
        <CardDescription>
          Recevez des alertes même quand l'app est fermée
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border">
          <div className="space-y-1">
            <p className="font-medium">Activer les notifications</p>
            <p className="text-sm text-muted-foreground">
              {isSubscribed 
                ? 'Vous recevrez des alertes pour les nouvelles commandes'
                : 'Activez pour être alerté instantanément'
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            <Switch
              checked={isSubscribed}
              onCheckedChange={handleToggle}
              disabled={isLoading || permission === 'denied'}
            />
          </div>
        </div>

        {/* Permission denied warning */}
        {permission === 'denied' && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Notifications bloquées</p>
              <p className="text-amber-700">
                Modifiez les paramètres de votre navigateur pour autoriser les notifications.
              </p>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Test button */}
        {isSubscribed && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleTest}
            className="w-full"
          >
            <BellRing className="w-4 h-4 mr-2" />
            Envoyer une notification test
          </Button>
        )}

        {/* Info about what notifications they'll receive */}
        {isSubscribed && (
          <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
            <p>📦 Nouvelle commande assignée</p>
            <p>🔄 Commande réassignée</p>
            <p>❌ Commande annulée</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PushNotificationsCard;
